import React from 'react';
import {useMatches} from '@remix-run/react';
import type {
  Product,
  ProductVariant,
  Image as ImageType,
  Maybe,
} from '@shopify/hydrogen/storefront-api-types';
import {Image} from '@shopify/hydrogen';
import clsx from 'clsx';
import * as R from 'ramda';
import {useLocation} from '@remix-run/react';

//components
import {Link} from 'components/Link';

//domains
import type {SanityProductQueryResponse} from 'domains/product';

export default function ProductDetailsColourOptions({
  selectedVariant,
}: {
  selectedVariant: ProductVariant;
}) {
  const matches = useMatches();
  const routeData = matches.find(
    (m) => m.id === 'routes/($lang)/products/$productHandle',
  )?.data;
  const product = routeData.product as Product & {
    selectedVariant?: ProductVariant;
  };
  const productSettings =
    routeData.productSettings as SanityProductQueryResponse['productSettings'];

  if (product.options) {
    return (
      <div className="flex-1 order-2 lg:order-1 lg:mr-4 w-full">
        {product.options.map((o, i) => {
          switch (o.name) {
            case 'Color':
            case 'Colour':
              return (
                <div
                  key={`${o.name}-${i}`}
                  className="flex flex-1 space-x-4 items-center"
                >
                  <span className="font-medium">
                    {productSettings?.product_settings_translations
                      ?.colour_label ?? 'Colour'}{' '}
                    :
                  </span>
                  <div className="flex-1 md:max-w-[400px]">
                    <ul className="grid grid-cols-5 gap-1 md:gap-2 lg:gap-1">
                      {o.values.map((ot, index) => {
                        const variantsOfThisColour =
                          product.variants.nodes.filter(
                            (v) =>
                              v.selectedOptions.find(
                                (vo) =>
                                  vo.name === 'Colour' || vo.name === 'Color',
                              )?.value === ot,
                          );
                        const variantOfThisColour = product.variants.nodes.find(
                          (v) =>
                            v.selectedOptions.find(
                              (vo) =>
                                vo.name === 'Colour' || vo.name === 'Color',
                            )?.value === ot,
                        );
                        const isThisColourAvailable = variantsOfThisColour.find(
                          (v) => v.availableForSale,
                        );
                        const firstAvailableSizeOfThisColour =
                          variantsOfThisColour
                            .find(
                              (v) =>
                                v.availableForSale &&
                                v.selectedOptions.find(
                                  (vo) =>
                                    vo.name === 'Size' ||
                                    vo.name === 'Model' ||
                                    vo.name === 'Type',
                                ),
                            )
                            ?.selectedOptions.find(
                              (vo) =>
                                vo.name === 'Size' ||
                                vo.name === 'Model' ||
                                vo.name === 'Type',
                            )?.value ??
                          selectedVariant?.selectedOptions.find(
                            (vo) =>
                              vo.name === 'Size' ||
                              vo.name === 'Model' ||
                              vo.name === 'Type',
                          )?.value;
                        const optionImage = variantOfThisColour?.image;
                        return (
                          <Option
                            optionName={o.name}
                            key={`${ot}-${index}`}
                            option={ot}
                            optionImage={optionImage}
                            available={!R.isNil(isThisColourAvailable)}
                            firstAvailableSizeOfThisColour={
                              firstAvailableSizeOfThisColour
                            }
                            selectedVariant={selectedVariant}
                          />
                        );
                      })}
                    </ul>
                  </div>
                </div>
              );
            // case 'Model':
            //   if (product.options.length === 1) {
            //     return (
            //       <div
            //         key={`generic-option-${o.name}`}
            //         className="order-2 flex w-full flex-1 space-x-4 lg:order-1 lg:mr-4 items-center"
            //       >
            //         <span className="font-medium">
            //           {productSettings?.product_settings_translations
            //             ?.colour_label ??
            //             o.name ??
            //             'Colour'}{' '}
            //           :
            //         </span>
            //         <div className="flex-1 md:max-w-[400px]">
            //           <ul className="grid grid-cols-5 gap-1 md:gap-2 lg:gap-1">
            //             {o.values.map((ot) => {
            //               const variantsOfThisColour =
            //                 product.variants.nodes.filter(
            //                   (v) =>
            //                     v.selectedOptions.find(
            //                       (vo) => vo.name === o.name,
            //                     )?.value === ot,
            //                 );
            //               const variantOfThisColour =
            //                 product.variants.nodes.find(
            //                   (v) =>
            //                     v.selectedOptions.find(
            //                       (vo) => vo.name === o.name,
            //                     )?.value === ot,
            //                 );
            //               const isThisColourAvailable =
            //                 variantsOfThisColour.find(
            //                   (v) => v.availableForSale,
            //                 );
            //               const optionImage = variantOfThisColour?.image;
            //               return (
            //                 <Option
            //                   optionName={o.name}
            //                   key={ot}
            //                   option={ot}
            //                   optionImage={optionImage}
            //                   available={!R.isNil(isThisColourAvailable)}
            //                   selectedVariant={selectedVariant}
            //                 />
            //               );
            //             })}
            //           </ul>
            //         </div>
            //       </div>
            //     );
            //   }
            //   return;
            default:
              return <></>;
          }
        })}
      </div>
    );
  }

  return <></>;
}

function Option({
  option,
  optionImage,
  available,
  optionName,
  firstAvailableSizeOfThisColour,
  selectedVariant,
}: {
  option: string;
  optionImage: Maybe<ImageType> | undefined;
  available: boolean;
  optionName: string;
  firstAvailableSizeOfThisColour?: string;
  selectedVariant: ProductVariant;
}) {
  const {search, pathname} = useLocation() as {
    search: string;
    pathname: string;
  };
  const params = new URLSearchParams(search);
  const isActive =
    selectedVariant?.selectedOptions.find((vo) => vo.name === optionName)
      ?.value === option ?? params.get(optionName) === option;

  if (firstAvailableSizeOfThisColour) {
    const secondVariantOptionName = selectedVariant?.selectedOptions.find(
      (vo) => vo.name === 'Size' || vo.name === 'Model' || vo.name === 'Type',
    )?.name;
    if (secondVariantOptionName) {
      params.set(secondVariantOptionName, firstAvailableSizeOfThisColour);
    } else {
      params.set('Size', firstAvailableSizeOfThisColour);
    }
  }

  params.set(optionName, option);
  return (
    <li
      title={option}
      className={clsx([
        'product-variant group cursor-pointer overflow-hidden rounded-full border-[1px] border-solid border-slate-200 p-1.5',
        !available && 'pointer-events-none opacity-25',
        isActive && '!border-2 !border-black',
      ])}
    >
      <Link to={`${pathname.replace('en/', '')}?${params.toString()}`}>
        <div className="product-variant__image relative aspect-w-16 aspect-h-16">
          <div className="transition-transform duration-200 ease-out group-hover:scale-110 z-1">
            {optionImage ? (
              <div className="absolute z-10 bg-white">
                <Image data={optionImage} width={300} height={300} />
              </div>
            ) : (
              <span className="flex items-center justify-center text-xs text-center">
                {option}
              </span>
            )}
          </div>
        </div>
      </Link>
    </li>
  );
}
